// Una fila de la tabla: la cabecera de la carga, plegable, con su detalle de
// líneas debajo. Pulsarla abre la ficha lateral de la carga.
import { useTranslation } from 'react-i18next'
import { Chevron } from '@/components/common/Chevron'
import { SubTableWrapper } from '@/components/common/SubTableWrapper'
import { MarcaCelda, useMarcada } from '@/components/common/MarcasFilas'
import type { CargaSalida, MarcasFilas } from '@/types'
import { CHAPA_RIESGO, clasesFilaCarga, enRiesgo } from '@/utils/cargasSalida'
import type { ColumnaCargaSalida, ContextoColumnas } from './columnasCargaSalida'
import { DetalleCargaSalida } from './DetalleCargaSalida'

interface Props {
  c: CargaSalida
  dark: boolean
  columnas: ColumnaCargaSalida[]
  hoy: Date
  abierta: boolean
  onToggle: () => void
  marcas: MarcasFilas
  clave: string
  /** Abre la ficha lateral de esta carga. */
  onFicha: () => void
}

/** ¿El clic cayó en algo que ya hace su propia cosa (botón, enlace, campo) o en
 *  la celda de la marca? Entonces NO abre la ficha: el chevron despliega, la
 *  marca marca, y abrir un cajón encima de cada una de esas acciones sería
 *  pelear con el gesto que el usuario quería. */
function clicEnControl(e: React.MouseEvent<HTMLTableRowElement>): boolean {
  const el = e.target as HTMLElement
  if (el.closest('button, a, input, select, textarea, label, [role="button"]')) return true
  return el.closest('td') === e.currentTarget.firstElementChild
}

export function FilaCargaSalida({
  c, dark, columnas, hoy, abierta, onToggle, marcas, clave,
  onFicha,
}: Props) {
  const { t } = useTranslation()
  const marcada = useMarcada(marcas, clave)
  const ctx: ContextoColumnas = { t, dark, hoy }
  const riesgo = enRiesgo(c, hoy)

  const tinte = clasesFilaCarga(c, dark, marcada, hoy)
  const hover = dark ? 'hover:bg-white/[0.04]' : 'hover:bg-white/70'
  const marcadoBg = dark ? 'fila-marcada-dark' : 'fila-marcada'
  const divider = dark ? 'border-white/8' : 'border-slate-200'
  const tdBase = 'px-2 py-1.5 text-[11px] align-middle whitespace-nowrap'

  return (
    <>
      <tr
        onClick={e => { if (!clicEnControl(e)) onFicha() }}
        className={`border-t ${divider} cursor-pointer transition-colors duration-150 ${marcada ? marcadoBg : (tinte.fondo || hover)} ${tinte.franja}`}
      >
        <MarcaCelda marcas={marcas} clave={clave} dark={dark} />
        <td className={tdBase}>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={abierta}
            className="flex items-center gap-1.5 cursor-pointer min-w-0"
          >
            <Chevron abierto={abierta} className={`w-3 h-3 shrink-0 ${dark ? 'text-slate-500' : 'text-slate-600'}`} />
            <span className={`font-mono text-[11px] font-semibold truncate ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
              {c.carga}
            </span>
            {riesgo && (
              <span
                title={t('cargasSalida.estado.atRisk')}
                className={`shrink-0 text-[8px] font-bold px-1 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
                  tinte.alarma ? CHAPA_RIESGO.sobreAlarma : (dark ? CHAPA_RIESGO.oscuro : CHAPA_RIESGO.claro)
                }`}
              >
                {t('cargasSalida.estado.atRisk')}
              </span>
            )}
          </button>
        </td>
        {columnas.map(col => (
          <td key={col.key} className={`${tdBase} ${col.alinear === 'right' ? 'text-right tabular-nums' : ''}`}>
            {col.render(c, ctx)}
          </td>
        ))}
      </tr>

      {abierta && (
        <SubTableWrapper dark={dark} colSpan={columnas.length + 2}>
          <DetalleCargaSalida c={c} dark={dark} />
        </SubTableWrapper>
      )}
    </>
  )
}
